/**
 * Controlled wrappers binding each input to react-hook-form.
 *
 * Always use these inside a `<Form>` rather than spreading `register()` yourself —
 * they wire the error message, `aria-invalid` and `aria-describedby` from the form
 * state, which is the part that gets forgotten and is the part a screen-reader user
 * depends on to know a field was rejected.
 */

import { Controller, type Control, type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import type { ReactNode } from 'react';

import { Checkbox } from '../components/Checkbox';
import { DatePicker } from '../components/DatePicker';
import { Input, type InputProps } from '../components/Input';
import { RadioGroup, type RadioGroupOption } from '../components/Radio';
import { Select, type SelectOption } from '../components/Select';
import { Textarea, type TextareaProps } from '../components/Textarea';
import { isRequiredField } from './Form';

type FieldName<T extends FieldValues> = Path<T>;

/**
 * Explicit `required` always wins; otherwise the schema decides.
 *
 * Derived rather than declared, so a field that cannot be left blank is marked as such
 * without anybody remembering to say so twice. Before this, nothing on the New case
 * form indicated that Client, Service, Responsible lawyer and Title were mandatory
 * until a failed submit turned them red -- the form only told you the rules after you
 * had broken them.
 */
function requiredFor<T extends FieldValues>(
  form: UseFormReturn<T>,
  name: FieldName<T>,
  explicit: boolean | undefined,
): boolean {
  return explicit ?? isRequiredField(form, String(name));
}

function messageFor<T extends FieldValues>(
  form: UseFormReturn<T>,
  name: FieldName<T>,
): string | undefined {
  const error = form.formState.errors[name];
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * `form` and `name` are also native HTML attributes on `<input>`, so every wrapper
 * below omits them from the underlying props. Without that, `form` is typed as
 * `UseFormReturn<T> & string` and nothing type-checks.
 */
export interface FormFieldBase<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: FieldName<T>;
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
}

export function FormInput<T extends FieldValues>({
  form,
  name,
  required,
  ...rest
}: FormFieldBase<T> & Omit<InputProps, 'label' | 'name' | 'error' | 'form'> & {
  label: ReactNode;
}) {
  return (
    <Input
      {...form.register(name)}
      error={messageFor(form, name)}
      required={requiredFor(form, name, required)}
      {...rest}
    />
  );
}

export function FormTextarea<T extends FieldValues>({
  form,
  name,
  required,
  ...rest
}: FormFieldBase<T> & Omit<TextareaProps, 'label' | 'name' | 'error' | 'form'> & {
  label: ReactNode;
}) {
  return (
    <Textarea
      {...form.register(name)}
      error={messageFor(form, name)}
      required={requiredFor(form, name, required)}
      {...rest}
    />
  );
}

export function FormSelect<T extends FieldValues>({
  form,
  name,
  options,
  placeholder,
  required,
  ...rest
}: FormFieldBase<T> & { options: SelectOption[]; placeholder?: string }) {
  return (
    <Select
      {...form.register(name)}
      options={options}
      placeholder={placeholder}
      error={messageFor(form, name)}
      required={requiredFor(form, name, required)}
      {...rest}
    />
  );
}

export function FormCheckbox<T extends FieldValues>({
  form,
  name,
  label,
  hint,
}: FormFieldBase<T>) {
  return (
    <Checkbox
      {...form.register(name)}
      label={label}
      hint={hint}
      error={messageFor(form, name)}
    />
  );
}

/**
 * Date and radio need `Controller`: both hold a value that is not simply the DOM
 * element's `value`, so `register` alone would not keep the form state in step.
 */
export function FormDate<T extends FieldValues>({
  form,
  name,
  label,
  hint,
  required,
  withTime,
}: FormFieldBase<T> & { withTime?: boolean }) {
  return (
    <Controller
      control={form.control as Control<T>}
      name={name}
      render={({ field }) => (
        <DatePicker
          label={label}
          hint={hint}
          required={required}
          withTime={withTime}
          value={(field.value as string) ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={messageFor(form, name)}
        />
      )}
    />
  );
}

export function FormRadioGroup<T extends FieldValues>({
  form,
  name,
  label,
  hint,
  required,
  options,
}: FormFieldBase<T> & { options: RadioGroupOption[] }) {
  return (
    <Controller
      control={form.control as Control<T>}
      name={name}
      render={({ field }) => (
        <RadioGroup
          name={String(name)}
          legend={label}
          hint={hint}
          required={required}
          options={options}
          value={(field.value as string) ?? ''}
          onChange={field.onChange}
          error={messageFor(form, name)}
        />
      )}
    />
  );
}
